import { getGuide } from 'dashboard/helper/onboardingGuide/registry';
import { stepMatchesRoute } from 'dashboard/helper/onboardingGuide/navigation';
import {
  readCompletedGuides,
  removeGuideCompletion,
  saveGuideCompletion,
} from 'dashboard/helper/onboardingGuide/completion';
import {
  readGuideSession,
  writeGuideSession,
  clearGuideSession,
} from 'dashboard/helper/onboardingGuide/guideSession';

// State of the guide the overlay is currently walking the user through.
// Separate from store/modules/onboarding.js (the task checklist) so the two
// can change independently while the old checklist is still in use.
//
// Completion stays manual. Ending a guide only clears the overlay; a guide
// that ended as finished leaves a completion prompt (the "done" card), and
// nothing is recorded unless the user clicks Mark as complete there or marks
// it from the onboarding list.

export const state = {
  activeGuideId: null,
  stepIndex: 0,
  accountId: null,
  // { guideId, reason: 'finished' | 'exited' } for the guide that ended last.
  lastEnded: null,
  // Ids of the guides marked complete for the current account, as loaded
  // from the completion storage (see helper/onboardingGuide/completion.js).
  completedGuideIds: [],
};

const persist = s =>
  writeGuideSession({
    guideId: s.activeGuideId,
    stepIndex: s.stepIndex,
    accountId: s.accountId,
  });

export const getters = {
  isGuideActive: s => !!s.activeGuideId,
  activeGuideId: s => s.activeGuideId,
  activeGuide: s => (s.activeGuideId ? getGuide(s.activeGuideId) : null),
  activeStep: (s, g) =>
    g.activeGuide ? g.activeGuide.steps[s.stepIndex] : null,
  stepIndex: s => s.stepIndex,
  stepCount: (s, g) => (g.activeGuide ? g.activeGuide.steps.length : 0),
  isFirstStep: s => s.stepIndex === 0,
  isLastStep: (s, g) => !!g.activeGuide && s.stepIndex === g.stepCount - 1,
  lastEnded: s => s.lastEnded,
  completedGuideIds: s => s.completedGuideIds,
  isGuideCompleted: s => guideId => s.completedGuideIds.includes(guideId),
  // The guide whose "done" card is showing: one that finished and hasn't
  // been marked complete or dismissed yet.
  completionPromptGuideId: s =>
    s.lastEnded && s.lastEnded.reason === 'finished'
      ? s.lastEnded.guideId
      : null,
  completionPromptGuide: (s, g) =>
    g.completionPromptGuideId ? getGuide(g.completionPromptGuideId) : null,
};

export const mutations = {
  SET_ACTIVE(s, { guideId, stepIndex, accountId }) {
    s.activeGuideId = guideId;
    s.stepIndex = stepIndex;
    s.accountId = accountId;
  },
  SET_STEP_INDEX(s, stepIndex) {
    s.stepIndex = stepIndex;
  },
  CLEAR_ACTIVE(s) {
    s.activeGuideId = null;
    s.stepIndex = 0;
    s.accountId = null;
  },
  SET_LAST_ENDED(s, lastEnded) {
    s.lastEnded = lastEnded;
  },
  SET_COMPLETED_GUIDES(s, guideIds) {
    s.completedGuideIds = guideIds;
  },
};

export const actions = {
  // Returns true when the guide exists and is now active, so the caller
  // knows whether to navigate to its first step.
  startGuide({ commit, state: s, rootGetters }, guideId) {
    const guide = getGuide(guideId);
    if (!guide) return false;
    commit('SET_LAST_ENDED', null);
    commit('SET_ACTIVE', {
      guideId,
      stepIndex: 0,
      accountId: rootGetters.getCurrentAccountId ?? null,
    });
    persist(s);
    return true;
  },

  // Picks a guide back up after a refresh. Anything stale (unknown guide, a
  // step that no longer exists, or a different account) is dropped.
  restoreGuide({ commit, state: s, rootGetters }) {
    if (s.activeGuideId) return false;
    const saved = readGuideSession();
    if (!saved) return false;

    const guide = getGuide(saved.guideId);
    const sameAccount =
      String(saved.accountId) === String(rootGetters.getCurrentAccountId);
    if (!guide || !sameAccount || saved.stepIndex >= guide.steps.length) {
      clearGuideSession();
      return false;
    }
    commit('SET_ACTIVE', saved);
    return true;
  },

  nextStep({ commit, dispatch, state: s, getters: g }) {
    if (!g.isGuideActive) return;
    if (g.isLastStep) {
      dispatch('endGuide', 'finished');
      return;
    }
    commit('SET_STEP_INDEX', s.stepIndex + 1);
    persist(s);
  },

  previousStep({ commit, state: s, getters: g }) {
    if (!g.isGuideActive || g.isFirstStep) return;
    commit('SET_STEP_INDEX', s.stepIndex - 1);
    persist(s);
  },

  // Keeps the guide in step with where the user actually is. Called on every
  // route change: landing on one of the guide's finish routes ends it, and
  // landing on a page that belongs to a later step jumps forward to that
  // step (clicking "New team" is what moves the guide on, not a button).
  // Going to an unrelated page, or back to an earlier one, changes nothing.
  syncToRoute({ commit, dispatch, state: s, getters: g }, route) {
    if (!g.isGuideActive || !g.activeGuide) return;
    const { finishRoutes = [], steps } = g.activeGuide;
    if (finishRoutes.includes(route?.name)) {
      dispatch('endGuide', 'finished');
      return;
    }
    if (stepMatchesRoute(g.activeStep, route)) return;
    const target = steps.findIndex(
      (step, index) => index > s.stepIndex && stepMatchesRoute(step, route)
    );
    if (target === -1) return;
    commit('SET_STEP_INDEX', target);
    persist(s);
  },

  // Jumps to a step by index (used to resume a paused guide).
  goToStep({ commit, state: s, getters: g }, index) {
    if (!g.isGuideActive || index < 0 || index >= g.stepCount) return;
    commit('SET_STEP_INDEX', index);
    persist(s);
  },

  // Records the finished guide as complete, on the user's click. Resolves to
  // false (and keeps the prompt up) when it could not be saved.
  markGuideComplete({ commit, getters: g, rootGetters }) {
    const guideId = g.completionPromptGuideId;
    if (!guideId) return false;
    const accountId = rootGetters.getCurrentAccountId;
    const saved = saveGuideCompletion({ accountId, guideId });
    if (saved) {
      commit('SET_COMPLETED_GUIDES', readCompletedGuides(accountId));
      commit('SET_LAST_ENDED', null);
    }
    return saved;
  },

  // Loads which guides the current account has marked complete.
  loadCompletedGuides({ commit, rootGetters }) {
    commit(
      'SET_COMPLETED_GUIDES',
      readCompletedGuides(rootGetters.getCurrentAccountId)
    );
  },

  // The onboarding list's Mark as complete / Undo for one guide. Resolves to
  // false when it could not be stored.
  setGuideCompleted({ commit, rootGetters }, { guideId, completed }) {
    const accountId = rootGetters.getCurrentAccountId;
    const save = completed ? saveGuideCompletion : removeGuideCompletion;
    const saved = save({ accountId, guideId });
    if (saved) commit('SET_COMPLETED_GUIDES', readCompletedGuides(accountId));
    return saved;
  },

  // "Not yet": closes the done card and marks nothing.
  dismissCompletionPrompt({ commit }) {
    commit('SET_LAST_ENDED', null);
  },

  endGuide({ commit, state: s }, reason = 'exited') {
    if (!s.activeGuideId) return;
    commit('SET_LAST_ENDED', { guideId: s.activeGuideId, reason });
    commit('CLEAR_ACTIVE');
    clearGuideSession();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
